import React, { useMemo, useEffect, useCallback } from 'react';
import moment from 'moment-timezone';
import { Button } from 'antd';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { PlusOutlined } from '@ant-design/icons';
import 'moment/locale/en-gb';

import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

import { getContrastRgbString, getRgbString } from '../../utils/colors';
import { DATE_DAILY_FORMAT } from '../../constants/date';
import CustomCalendarToolbar from '../shared/CustomCalendarToolbar';
import UpdateLabelModal from '../../containers/labels/UpdateLabelModal';
import DocsLink from '../docs/DocsLink';
import docsBookmarks from '../../constants/docs';
import SimpleNavigationBar from '../navigation/SimpleNavigationBar';

const DragAndDropCalendar = withDragAndDrop(Calendar);

const LabelsView = ({
  modalVisible,
  labels,
  zoneId,
  hasPermission,
  currentDate,
  onDateSelect,
  onLabelSelect,
  onLabelResize,
  setLabelsCurrentDate,
  fetchLabels,
}) => {
  useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  const dateLabels = useMemo(
    () =>
      labels.map((label) => ({
        ...label,
        start: new Date(moment(label.duration[0], DATE_DAILY_FORMAT).valueOf()),
        end: new Date(moment(label.duration[1], DATE_DAILY_FORMAT).add(1, 'day').valueOf()),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [labels, zoneId]
  );

  const getEventColor = useCallback((event) => {
    const eventColor = event?.color;
    return {
      className: 'st-calendar-event',
      style: {
        background: getRgbString(eventColor),
        color: getContrastRgbString(eventColor),
      },
    };
  }, []);

  const onSelectEvent = useCallback(
    (event) => {
      if (onLabelSelect) {
        // удаляем поле start и end
        const { start, end, ...restEvent } = event;
        onLabelSelect(restEvent);
      }
    },
    [onLabelSelect]
  );

  const onSelectSlot = useCallback(
    (event) => {
      if (onDateSelect) {
        const { start, end } = event;
        const getFormattedDate = (date) => {
          return moment(date.valueOf()).format(DATE_DAILY_FORMAT);
        };
        const formattedDates = [getFormattedDate(start), getFormattedDate(end)];

        onDateSelect(formattedDates);
      }
    },
    [onDateSelect]
  );

  const renderActions = () => (
    <div>
      <Button icon={<PlusOutlined />} onClick={() => onDateSelect()} disabled={!hasPermission}>
        Add label
      </Button>
      <DocsLink
        scrolTo={docsBookmarks.labels}
        styleIcon={{ fontSize: 20, marginLeft: 5, color: 'gray' }}
      />
    </div>
  );

  const renderToolbar = (toolbar) => (
    <CustomCalendarToolbar toolbar={toolbar} extraRender={renderActions} />
  );
  return (
    <>
      <SimpleNavigationBar />
      <div style={{ height: 'calc(100% - 63px)', padding: '20px' }}>
        <DragAndDropCalendar
          localizer={momentLocalizer(moment)}
          events={dateLabels}
          defaultDate={currentDate ? new Date(currentDate) : new Date()}
          views={['month']}
          culture={zoneId}
          onNavigate={(currentDateParam) => setLabelsCurrentDate(currentDateParam.getTime())}
          components={{
            toolbar: renderToolbar,
          }}
          selectable={!modalVisible}
          popup
          eventPropGetter={getEventColor}
          onEventDrop={onLabelResize}
          onEventResize={onLabelResize}
          onSelectEvent={onSelectEvent}
          onSelectSlot={onSelectSlot}
        />
        {modalVisible && <UpdateLabelModal />}
      </div>
    </>
  );
};

export default LabelsView;
